import { useTranslation } from 'next-i18next'

import { ListingCardProps } from '../ListingCard.types'
import { BadgeType } from '../micro/Badge'
import { FormatOptions } from '../utils/formatDetails'

interface VariantConfig {
  badges: Set<BadgeType>
  priceOptions: FormatOptions
  ctaLabel: string
  isBlurred: boolean
  showCheckbox: boolean
}
export interface UseListingCardResult {
  config: VariantConfig
}

/**
 * Hook that encapsulates variant-specific logic and config lookup for ListingCard.
 * Uses discriminated union narrowing to safely access props per variant.
 */
export function useListingCard(props: ListingCardProps): UseListingCardResult {
  const { t } = useTranslation()
  const VARIANT_CONFIG: Record<ListingCardProps['variant'], VariantConfig> = {
    default: {
      badges: new Set(),
      priceOptions: {},
      ctaLabel: t('Send Message'),
      isBlurred: false,
      showCheckbox: false
    },
    sponsored: {
      badges: new Set(['sponsored']),
      priceOptions: {},
      ctaLabel: t('Send Message'),
      isBlurred: false,
      showCheckbox: false
    },
    building: {
      badges: new Set(['unitsAvailable']),
      priceOptions: {},
      ctaLabel: t('View Available Units'),
      isBlurred: false,
      showCheckbox: false
    },
    multiUnit: {
      badges: new Set(['unitsAvailable']),
      priceOptions: { plusIfNoMax: true },
      ctaLabel: t('Check Availability'),
      isBlurred: false,
      showCheckbox: false
    },
    variousRates: {
      badges: new Set(['unitsAvailable']),
      priceOptions: {},
      ctaLabel: t('View Available Units'),
      isBlurred: false,
      showCheckbox: false
    },
    rented: {
      badges: new Set(),
      priceOptions: {},
      ctaLabel: t('View Listing Details'),
      isBlurred: false,
      showCheckbox: false
    },
    locked: {
      badges: new Set(),
      priceOptions: {},
      ctaLabel: t('Send Message'),
      isBlurred: true,
      showCheckbox: false
    },
    select: {
      badges: new Set(),
      priceOptions: {},
      ctaLabel: '',
      isBlurred: false,
      showCheckbox: true
    }
  }

  const variant = props.variant

  if (!variant) {
    throw new Error('ListingCard requires a variant prop')
  }

  const baseConfig = VARIANT_CONFIG[variant]

  if (!baseConfig) {
    throw new Error(`Invalid variant on ListingCard: ${variant}`)
  }

  const badges = new Set(baseConfig.badges)
  if (props.specialOffer === true) {
    badges.add('specialOffer')
  }

  const instanceConfig: VariantConfig = {
    ...baseConfig,
    badges,
    ctaLabel: baseConfig.ctaLabel
  }

  return { config: instanceConfig }
}
